<template>
  <div class="card">
    <div class="card-header">
      <h3>Linked Rides</h3>
      <small class="form-text text-muted">
        "Fare charged" is always the all-in, tax-included figure the rider was actually charged — the
        pre-tax subtotal below it is an informational breakdown only, never the headline number.
      </small>
    </div>
    <div class="card-body">
      <div v-if="isLoading" class="text-center py-3">
        <span class="spinner-border spinner-border-sm me-2"></span> Loading...
      </div>
      <div v-else-if="rows.length === 0" class="text-muted">No rides linked to this wallet yet.</div>
      <div v-else class="table-responsive">
        <table class="table table-borderless">
          <thead>
          <tr>
            <th>Date</th>
            <th>Settlement</th>
            <th>Fare Charged</th>
            <th>Breakdown</th>
            <th>Wallet</th>
            <th>Card</th>
            <th>Outstanding</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="row in rows" :key="row.rideId">
            <td>{{ formatDate(row.orderDateTime) }}</td>
            <td>{{ settlementLabel(row.settlementStatus) }}</td>
            <td class="fw-bold" v-html="minorCurrency(row.fareChargedMinor)"></td>
            <td class="text-muted small">
              Subtotal (pre-tax): {{ formatMinor(row.subtotalPreTaxMinor) }}
              &middot; {{ row.taxLabel || 'Tax' }}: {{ formatMinor(row.taxMinor) }}
            </td>
            <td v-html="minorCurrency(row.walletAmountMinor)"></td>
            <td v-html="minorCurrency(row.cardAmountMinor)"></td>
            <td v-html="minorCurrency(row.outstandingMinor)"></td>
          </tr>
          </tbody>
        </table>
      </div>

      <nav v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-2">
        <button class="btn btn-outline-secondary btn-sm" :disabled="page <= 1" @click="changePage(page - 1)">Previous</button>
        <span class="text-muted small">Page {{ page }} of {{ totalPages }} ({{ totalRows }} rides)</span>
        <button class="btn btn-outline-secondary btn-sm" :disabled="!hasNext" @click="changePage(page + 1)">Next</button>
      </nav>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { minorCurrency } from '../utility/DatatableRenderer';

const props = defineProps({
  walletId: { type: String, required: true }
});

const rows = ref([]);
const page = ref(1);
const totalPages = ref(0);
const totalRows = ref(0);
const hasNext = ref(false);
const isLoading = ref(false);

const formatDate = (value) => value ? new Date(value).toLocaleString() : '';
const formatMinor = (value) => (value === null || value === undefined) ? '$0.00' : `$${(value / 100).toFixed(2)}`;

// NOT_APPLICABLE is a real, expected value here (every card-only ride), not an error state — the
// #167 guide's mobile-only table omits it, but the admin viewer reads settlementStatus directly.
const settlementLabel = (status) => status === 'NOT_APPLICABLE' ? 'Card only' : (status || '—');

const load = async () => {
  isLoading.value = true;
  try {
    const response = await fetch(`/api/admin/wallets/${props.walletId}/ride-links?page=${page.value}`);
    if (response.ok) {
      const data = await response.json();
      rows.value = data.items || [];
      totalPages.value = data.totalPages || 0;
      totalRows.value = data.totalRows || 0;
      hasNext.value = !!data.hasNext;
    }
  } catch (error) {
    console.error('Error loading wallet ride links:', error);
  } finally {
    isLoading.value = false;
  }
};

const changePage = (next) => {
  if (next < 1) return;
  page.value = next;
  load();
};

onMounted(load);

defineExpose({ load });
</script>
